import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Flame, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

import api, { API } from "../lib/api";
import { formatIDR } from "../lib/format";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

/**
 * Hitung sisa waktu sampai `endsAt`.
 * Dipisah jadi hari/jam/menit/detik supaya bisa dirender per-digit di UI.
 */
function getRemaining(endsAt) {
  const total = Math.max(0, new Date(endsAt).getTime() - Date.now());
  const s = Math.floor(total / 1000);
  return {
    total,
    ended: total <= 0,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

const pad = (n) => String(n).padStart(2, "0");

/** Countdown yang tick tiap detik; memanggil `onEnd` sekali saat waktunya habis. */
function useCountdown(endsAt, onEnd) {
  const [left, setLeft] = useState(() => getRemaining(endsAt));
  const firedRef = useRef(false);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    firedRef.current = false;
    setLeft(getRemaining(endsAt));
    const t = setInterval(() => {
      const next = getRemaining(endsAt);
      setLeft(next);
      if (next.ended && !firedRef.current) {
        firedRef.current = true;
        onEndRef.current?.();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [endsAt]);

  return left;
}

/** Satu blok digit countdown (kotak hitam ala papan skor). */
function TimeBox({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="min-w-[34px] md:min-w-[42px] px-1.5 md:px-2 py-1 md:py-1.5 bg-black border border-[#FFD700]/50 text-[#FFD700] font-mono font-black text-base md:text-xl leading-none text-center tabular-nums">
        {pad(value)}
      </div>
      <span className="mt-1 text-[8px] md:text-[9px] uppercase tracking-[0.15em] text-white/40">
        {label}
      </span>
    </div>
  );
}

/** Countdown besar di header section (mengikuti flash sale yang paling cepat berakhir). */
function HeaderCountdown({ endsAt, onEnd }) {
  const left = useCountdown(endsAt, onEnd);
  return (
    <div className="flex items-center gap-1.5 md:gap-2" data-testid="flash-sale-header-countdown">
      {left.days > 0 && <TimeBox value={left.days} label="Hari" />}
      <TimeBox value={left.hours} label="Jam" />
      <TimeBox value={left.minutes} label="Menit" />
      <TimeBox value={left.seconds} label="Detik" />
    </div>
  );
}

/** Countdown ringkas di dalam kartu produk. */
function CardCountdown({ endsAt, onEnd }) {
  const left = useCountdown(endsAt, onEnd);
  const urgent = left.total > 0 && left.total <= 60 * 60 * 1000; // < 1 jam
  const text =
    left.days > 0
      ? `${left.days}h ${pad(left.hours)}:${pad(left.minutes)}:${pad(left.seconds)}`
      : `${pad(left.hours)}:${pad(left.minutes)}:${pad(left.seconds)}`;
  return (
    <span
      className={`font-mono font-bold tabular-nums ${
        left.ended ? "text-white/40" : urgent ? "text-[#FF003C] animate-pulse" : "text-[#FFD700]"
      }`}
    >
      {left.ended ? "BERAKHIR" : text}
    </span>
  );
}

function FlashCard({ item, onExpire }) {
  const p = item.product || {};
  const remaining = Math.max(0, item.remaining ?? 0);
  const total = Math.max(1, item.stock ?? 1);
  const soldPercent = Math.min(100, Math.max(0, Math.round(((total - remaining) / total) * 100)));
  const almostGone = remaining > 0 && remaining <= Math.max(1, Math.ceil(total * 0.2));

  return (
    <Link
      to={`/product/${p.id}`}
      data-testid={`flash-sale-card-${item.id}`}
      className="group relative shrink-0 snap-start w-[190px] sm:w-[230px] md:w-[250px] bg-[#0a0a0a] border border-[#FF003C]/40 hover:border-[#FFD700] transition-all duration-200 hover:-translate-y-1 overflow-hidden"
      style={{ boxShadow: "0 0 20px rgba(255,0,60,0.12)" }}
    >
      <div className="aspect-square bg-black overflow-hidden relative">
        {p.image_url ? (
          <img
            src={resolveImg(p.image_url)}
            alt={p.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Badge diskon — sudut kiri atas, warna panas biar mencolok */}
        <div className="absolute top-0 left-0">
          <div
            className="flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-[#FF003C] to-[#FF6B00] text-white font-display font-black text-xs md:text-sm leading-none"
            style={{ clipPath: "polygon(0 0, 100% 0, 88% 100%, 0 100%)", paddingRight: "18px" }}
          >
            <Flame className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
            {item.discount_percent}%
          </div>
        </div>

        {/* Sisa waktu di atas gambar */}
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-1 text-[10px] md:text-[11px]">
          <span className="inline-flex items-center gap-1 px-1.5 py-1 bg-black/70 backdrop-blur-sm border border-white/10">
            <Zap className="w-3 h-3 text-[#FFD700]" />
            <CardCountdown endsAt={item.ends_at} onEnd={onExpire} />
          </span>
          {almostGone && (
            <span className="px-1.5 py-1 bg-[#FF003C] text-white font-black uppercase tracking-wider text-[9px] animate-pulse">
              Sisa {remaining}
            </span>
          )}
        </div>
      </div>

      <div className="p-2.5 md:p-3">
        <h3 className="font-semibold text-xs md:text-sm text-white line-clamp-2 min-h-[32px] md:min-h-[40px] leading-snug group-hover:text-[#FFD700] transition-colors">
          {p.title}
        </h3>

        <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
          <span className="font-display font-black text-base md:text-lg text-[#FF003C] leading-none">
            {formatIDR(item.flash_price)}
          </span>
          <span className="text-[10px] md:text-xs text-white/40 line-through leading-none">
            {formatIDR(item.original_price)}
          </span>
        </div>
        <div className="mt-1 text-[9px] md:text-[10px] uppercase tracking-widest text-[#00FF66]">
          Hemat {formatIDR(item.saving)}
        </div>

        {/* Progress bar stok flash */}
        <div className="mt-2 md:mt-2.5">
          <div className="relative h-3.5 md:h-4 bg-white/10 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FF003C] to-[#FF6B00] transition-all duration-500"
              style={{ width: `${Math.max(soldPercent, 6)}%` }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-[8px] md:text-[9px] font-black uppercase tracking-wider text-white drop-shadow">
              {remaining > 0 ? `Sisa ${remaining} dari ${total}` : "Habis"}
            </span>
          </div>
        </div>

        <p className="mt-1.5 text-[10px] text-white/40 truncate">{p.seller_name}</p>
      </div>
    </Link>
  );
}

/**
 * Section Flash Sale untuk halaman utama.
 *
 * Data diambil dari `GET /api/flash-sales` (hanya entri yang benar-benar live).
 * Section otomatis hilang saat tidak ada flash sale aktif, dan me-refresh
 * dirinya sendiri ketika ada kartu yang countdown-nya habis atau stok flash
 * ludes — produknya lalu kembali dijual dengan harga normal.
 */
export default function FlashSaleSection() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/flash-sales?limit=12");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    // Sinkronisasi berkala supaya stok yang terjual buyer lain ikut terupdate.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Flash sale yang paling cepat berakhir → dipakai untuk countdown header.
  const soonest = useMemo(() => {
    if (!items.length) return null;
    return items.reduce((acc, it) =>
      new Date(it.ends_at).getTime() < new Date(acc.ends_at).getTime() ? it : acc
    );
  }, [items]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  if (!loaded || items.length === 0) return null;

  return (
    <section
      className="relative border-b border-white/10 overflow-hidden"
      data-testid="home-flash-sale"
    >
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 15% 0%, rgba(255,0,60,0.22) 0%, transparent 55%), radial-gradient(ellipse at 85% 100%, rgba(255,107,0,0.16) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-14">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-5 md:mb-8">
          <div>
            <span
              className="tag inline-flex items-center gap-1.5"
              style={{
                borderColor: "rgba(255,0,60,0.6)",
                color: "#FF6B6B",
                background: "rgba(255,0,60,0.12)",
              }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF003C] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF003C]" />
              </span>
              LIVE · {items.length} DEAL
            </span>

            <h2 className="font-display font-black text-2xl md:text-5xl tracking-tighter text-white mt-2 flex items-center gap-2 md:gap-3">
              <Flame className="w-6 h-6 md:w-9 md:h-9 text-[#FF003C] animate-pulse" />
              <span>
                FLASH<span className="text-[#FF003C]"> SALE</span>
              </span>
            </h2>
            <p className="mt-2 text-sm md:text-base text-white/60 max-w-xl">
              Harga jatuh, stok terbatas. Kalau stoknya habis atau waktunya lewat, harga balik normal.
            </p>
          </div>

          <div className="flex items-end gap-3 md:gap-4">
            <div className="text-right">
              <div className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1.5">
                Berakhir dalam
              </div>
              {soonest && <HeaderCountdown endsAt={soonest.ends_at} onEnd={load} />}
            </div>
            <div className="hidden md:flex items-center gap-2 pb-1">
              <button
                type="button"
                aria-label="Sebelumnya"
                onClick={() => scrollBy(-1)}
                className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:border-[#FF003C] hover:text-[#FF003C] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                aria-label="Berikutnya"
                onClick={() => scrollBy(1)}
                className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:border-[#FF003C] hover:text-[#FF003C] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory scroll-smooth home-auction-scroll"
        >
          {items.map((it) => (
            <FlashCard key={it.id} item={it} onExpire={load} />
          ))}
        </div>

        <div className="mt-2">
          <Link
            to="/browse"
            data-testid="flash-sale-browse-all"
            className="inline-flex items-center gap-2 text-xs md:text-sm font-display font-bold uppercase tracking-[0.15em] text-white/70 hover:text-[#FF003C] transition-colors"
          >
            Lihat produk lainnya <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
